import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import type { Order, Table } from '@/types/common';
import { formatCurrency } from '@/utils/format';
import Clock from '@/components/Clock/Clock';
import { usePWA } from '@/hooks/usePWA';


// Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function HomePage() {
  const { profile, isLoading: isAuthLoading } = useAuth();
  const [tables, setTables] = useState<Table[]>([]);
  const [activeOrders, setActiveOrders] = useState<Order[]>([]);
  const [todaysRevenue, setTodaysRevenue] = useState(0);
  const [loading, setLoading] = useState(true);
  const { isInstallable, installApp } = usePWA();
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    try {
      // Fetch tables
      const { data: tablesData } = await supabase
        .from('tables')
        .select('*')
        .order('number');

      // Fetch active orders
      const { data: ordersData } = await supabase
        .from('orders')
        .select('*')
        .not('status', 'eq', 'paid')
        .order('created_at', { ascending: false });

      // Fetch today's revenue
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { data: revenueData } = await supabase
        .from('orders')
        .select('total')
        .eq('status', 'paid')
        .gte('created_at', today.toISOString());

      if (tablesData) setTables(tablesData);
      if (ordersData) setActiveOrders(ordersData);
      if (revenueData) {
        const total = revenueData.reduce((sum, order) => sum + order.total, 0);
        setTodaysRevenue(total);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard data initially and set up polling
  useEffect(() => {
    // Don't fetch data until auth is ready
    if (isAuthLoading) return;

    fetchDashboardData();

    // Poll every 30 seconds
    const pollInterval = setInterval(() => {
      fetchDashboardData();
    }, 30000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [isAuthLoading]);

  // Chart data
  const chartData = {
    labels: ['Available', 'Occupied', 'Reserved'],
    datasets: [{
      label: 'Tables Status',
      data: [
        tables.filter(t => t.status === 'available').length,
        tables.filter(t => t.status === 'occupied').length,
        tables.filter(t => t.status === 'reserved').length,
      ],
      backgroundColor: [
        'rgba(34, 197, 94, 0.5)',  // green
        'rgba(239, 68, 68, 0.5)',   // red
        'rgba(234, 179, 8, 0.5)',   // yellow
      ],
      borderColor: [
        'rgb(34, 197, 94)',
        'rgb(239, 68, 68)',
        'rgb(234, 179, 8)',
      ],
      borderWidth: 1,
    }],
  };

  if (isAuthLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen-75">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-4">
      <div className="space-y-6">
        {/* Header with Clock and Welcome message */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {/* Clock */}
          <div className="bg-white dark:bg-dark-bg-light overflow-hidden shadow rounded-lg p-6">
            <Clock />
          </div>

          {/* Welcome message and New Order button */}
          <div className="md:col-span-2 bg-white dark:bg-dark-bg-light overflow-hidden shadow rounded-lg p-6 h-full">
            <div className="flex items-center justify-between h-full">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Welcome back, {profile?.email?.split('@')[0] || 'User'}
                </h2>
                <p className="text-sm text-gray-500">Here's what's happening today</p>
              </div>
              <Link
                to="/orders/new"
                className="inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-white rounded-lg transition-all duration-300 ease-out bg-gradient-to-r from-cyan-500 to-blue-500 shadow-[0_4px_0_0_#0369a1] hover:shadow-[0_6px_0_0_#0369a1] hover:-translate-y-[2px] active:translate-y-[2px] active:shadow-[0_2px_0_0_#0369a1] touch-manipulation"
              >
                <i className="fas fa-plus mr-2 sm:mr-3" aria-hidden="true" />
                <span>New Order</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {/* Today's Revenue */}
          <div 
            onClick={() => navigate('/analytics?timeframe=day')}
            className="bg-white dark:bg-dark-bg-light overflow-hidden shadow rounded-lg cursor-pointer 
              hover:shadow-md transition-shadow duration-200"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="fas fa-euro-sign text-2xl text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary truncate">
                      Today's Revenue
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                        {formatCurrency(todaysRevenue)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-dark-bg-lighter px-5 py-3">
              <div className="text-sm">
                <span className="font-medium text-gray-500 dark:text-dark-text-secondary">
                  Updated in real-time
                </span>
              </div>
            </div>
          </div>

          {/* Active Orders */}
          <div 
            onClick={() => navigate('/orders?status=active')}
            className="bg-white dark:bg-dark-bg-light overflow-hidden shadow rounded-lg cursor-pointer 
              hover:shadow-md transition-shadow duration-200"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="fas fa-clipboard-list text-2xl text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary truncate">
                      Active Orders
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                        {activeOrders.length}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-dark-bg-lighter px-5 py-3">
              <div className="text-sm">
                <span className="font-medium text-primary-600 hover:text-primary-500">
                  View all orders
                  <i className="fas fa-arrow-right ml-2" />
                </span>
              </div>
            </div>
          </div>

          {/* Available Tables */}
          <div 
            onClick={() => navigate('/tables')}
            className="bg-white dark:bg-dark-bg-light overflow-hidden shadow rounded-lg cursor-pointer 
              hover:shadow-md transition-shadow duration-200"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="fas fa-chair text-2xl text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary truncate">
                      Available Tables
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
                        {tables.filter(t => t.status === 'available').length} / {tables.length}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-dark-bg-lighter px-5 py-3">
              <div className="text-sm">
                <span className="font-medium text-primary-600 hover:text-primary-500">
                  View all tables
                  <i className="fas fa-arrow-right ml-2" />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tables Chart */}
        <div className="bg-white dark:bg-dark-bg-light shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-4">
            Table Status Overview
          </h2>
          <div className="h-64">
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Active Orders List */}
        <div className="bg-white dark:bg-dark-bg-light shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">
              Recent Orders
            </h2>
          </div>
          <div className="border-t border-gray-200 dark:border-dark-border">
            <ul className="divide-y divide-gray-200 dark:divide-dark-border">
              {activeOrders.slice(0, 5).map((order) => (
                <li key={order.id}>
                  <Link
                    to={`/orders/${order.id}`}
                    className="block hover:bg-gray-50 dark:hover:bg-dark-bg-lighter"
                  >
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-primary-600 truncate">
                            Order #{order.id.slice(0, 8)}
                          </p>
                          <div className="ml-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              order.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200' :
                              order.status === 'preparing' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' :
                              order.status === 'ready' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200'
                            }`}>
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
                            {formatCurrency(order.total)}
                          </p>
                          <i className="fas fa-chevron-right ml-2 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            {activeOrders.length > 5 && (
              <div className="bg-gray-50 dark:bg-dark-bg-lighter px-4 py-3 text-center">
                <Link
                  to="/orders"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  View all orders
                  <i className="fas fa-arrow-right ml-2" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}